import React from 'react';
import { Button, Col, Divider, Row, Typography } from 'antd';
import { ADMIN_ROUTES } from 'constants/routes';
import styles from './styles.module.scss';
import { HasAccess, IconWrapper, Image, Tag } from 'components';
import {
  AlternativeIcon,
  DeleteIcon,
  MarkNotAvailableIcon,
  StarIcon,
  SubstituteIcon,
  VerifiedIcon,
} from 'components/common/icons';
import dayjs from 'dayjs';
import { PERMISSIONS } from 'constants/permissions';
import { DeleteModal } from './delete';
import { useSupplierOfferCard } from 'feature/purchaseRequestTabs/supplierOffers/supplierOfferCard/useSupplierOfferCard';
import { usePurchaseRequestByIdQuery } from 'api';
import { SUPPLIER_OFFER_SOURCE } from 'models/SupplierOffer';
import { PublishSupplierOfferModal } from 'pages/admin/supplierOffers/edit/publishSupplierOffer';

const { Text } = Typography;

interface ISupplierOfferCard {
  supplierOffer: Record<string, any>;
}

export const SupplierOfferCard = React.memo((props: ISupplierOfferCard) => {
  const { supplierOffer } = props;
  const { handleStarChange, recordId, navigate, onPublish, id, publishModalState, deleteModalState, onDelete } =
    useSupplierOfferCard();
  const { data: { purchaseRequest } = {} } = usePurchaseRequestByIdQuery({ id });

  return (
    <>
      <Col span={24} className={styles.main}>
        <Row gutter={[0, 12]}>
          <Col span={24}>
            <Row gutter={12} align={'middle'} justify={'space-between'}>
              <Col>
                <Row>
                  <Col>
                    <Text type={'secondary'}>Offer Number: {supplierOffer.uuid}</Text>
                  </Col>
                  <Divider type={'vertical'} />
                  <Col>
                    <Row align={'middle'} className={'gap-4'}>
                      <Text type={'secondary'}>Created by: {supplierOffer.createdBy}</Text>
                      <Text type={'secondary'}>on {dayjs(supplierOffer.createDate).format('YYYY-MM-DD')}</Text>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col>
                <Row gutter={4} align={'middle'}>
                  <HasAccess role={PERMISSIONS.purchaseRequests.supplierOffers.write}>
                    <Col>
                      <Button
                        onClick={() => handleStarChange(supplierOffer.supplierOfferId)}
                        style={{ backgroundColor: '#EFF2F8' }}
                        type={'text'}
                        size={'small'}
                        disabled={purchaseRequest?.isFinalStatus}
                        icon={<StarIcon checked={supplierOffer.star} />}
                      />
                    </Col>
                  </HasAccess>
                  <HasAccess role={PERMISSIONS.purchaseRequests.supplierOffers.delete}>
                    <Col>
                      <Button
                        style={{ backgroundColor: '#EFF2F8' }}
                        onClick={() => onDelete(supplierOffer.supplierOfferId)}
                        type={'text'}
                        size={'small'}
                        disabled={purchaseRequest?.isFinalStatus}
                        icon={<DeleteIcon size={16} />}
                      />
                    </Col>
                  </HasAccess>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={40} align={'bottom'}>
              <Col span={12}>
                <Row gutter={[0, 10]} align={'bottom'}>
                  <Col span={24}>
                    <Row gutter={8} align={'middle'}>
                      {supplierOffer?.supplier?.files?.LOGO && (
                        <Col className={styles.supplier_logo}>
                          <Image src={supplierOffer.supplier.files.LOGO} preview={false} alt={'Logo'} />
                        </Col>
                      )}
                      <Col>
                        <Row align={'middle'} className={'gap-8'}>
                          <Text className={'font-size-18 font-weight-700'}>{supplierOffer.supplier.name}</Text>
                          {supplierOffer.supplier.verified && <VerifiedIcon />}
                        </Row>
                      </Col>
                      <Col>
                        {supplierOffer.supplier.mainSupplier && (
                          <Text className={styles.main_supplier_tag}>Main Supplier</Text>
                        )}
                      </Col>
                      <Col>
                        {supplierOffer.supplier.priceReferenceSupplier && (
                          <Text className={styles.price_reference_supplier_tag}>Price Reference Supplier</Text>
                        )}
                      </Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Row gutter={12} align={'middle'}>
                      {supplierOffer.supplier?.country?.fileLocation && (
                        <>
                          <Col>
                            <IconWrapper src={supplierOffer.supplier.country.fileLocation} />
                          </Col>
                          <Divider type={'vertical'} />
                        </>
                      )}
                      <Col>
                        <Text type={'secondary'}>Request Count: {supplierOffer.supplier.requestsCount}</Text>
                      </Col>
                      <Divider type={'vertical'} />
                      <Col>
                        <Text type={'secondary'}>
                          Turnover: {supplierOffer.supplier.turnover ? `${supplierOffer.supplier.turnover}€` : 'N/A'}
                        </Text>
                      </Col>
                      <Divider type={'vertical'} />
                      <Col>
                        <Text type={'secondary'}>
                          Conversion Rate:
                          {supplierOffer.supplier.conversionRate ? `${supplierOffer.supplier.conversionRate}%` : 'N/A'}
                        </Text>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Row className={'gap-6'}>
                      {supplierOffer.supplier.tags?.map((_tag: string, index: number) => (
                        <Tag title={_tag} key={index + _tag} className={'custom-tag'}>
                          {_tag}
                        </Tag>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Row gutter={[0, 16]} align={'bottom'}>
                  <Col span={24}>
                    <Row justify={'space-between'} align={'bottom'} gutter={10}>
                      <Col span={12}>
                        <Row>
                          <Col span={24} className={styles.fee_info_item}>
                            <Row justify={'space-between'}>
                              <Col>
                                <Row align={'middle'} gutter={4}>
                                  <Col>
                                    <Text type={'secondary'}>Items</Text>
                                  </Col>
                                  <Col>
                                    <Row align={'middle'} className={'gap-2'}>
                                      {supplierOffer.offer.hasAlternative && <AlternativeIcon />}
                                      {supplierOffer.offer.hasSubstitute && <SubstituteIcon />}
                                      {supplierOffer.offer.hasNotAvailable && (
                                        <MarkNotAvailableIcon color={'#FB3748'} />
                                      )}
                                    </Row>
                                  </Col>
                                </Row>
                              </Col>
                              <Col>
                                <Text type={'secondary'}>
                                  {supplierOffer.offer.itemsCount ? `${supplierOffer.offer.itemsCount}€` : 'N/A'}
                                </Text>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24} className={styles.fee_info_item}>
                            <Row justify={'space-between'}>
                              <Col>
                                <Text type={'secondary'}>Shipping cost</Text>
                              </Col>
                              <Col>
                                <Text type={'secondary'}>
                                  {supplierOffer.offer.shippingCost ? `${supplierOffer.offer.shippingCost}€` : 'N/A'}
                                </Text>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24} className={styles.fee_info_item}>
                            <Row justify={'space-between'}>
                              <Col>
                                <Text type={'secondary'}>Additional fees</Text>
                              </Col>
                              <Col>
                                <Text type={'secondary'}>
                                  {supplierOffer.offer.additionalFees
                                    ? `${supplierOffer.offer.additionalFees}€`
                                    : 'N/A'}
                                </Text>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={12}>
                        <Row justify={'end'}>
                          <Col span={24}>
                            <Row justify={'end'} style={{ alignItems: 'baseline' }} className={'gap-2'}>
                              <Text className={'font-size-16'}>
                                {supplierOffer.offer.hasMultipleTotals ? 'From' : ''}
                              </Text>
                              <Text className={'font-size-30 font-weight-700'}>
                                {supplierOffer.offer.total ? `${supplierOffer.offer.total.toFixed(2)}€` : 'N/A'}
                              </Text>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row justify={'end'}>
                              <Text type={'secondary'}>
                                Valid until:
                                {supplierOffer.offer.offerValidityDate
                                  ? dayjs(supplierOffer.offer.offerValidityDate).format('YYYY-MM-DD')
                                  : 'N/A'}
                              </Text>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row justify={'end'}>
                              <Text className={'font-size-16 font-weight-600'} style={{ color: '#05796B' }}>
                                {supplierOffer.offer.leadTimeMin && supplierOffer.offer.leadTimeMax
                                  ? `${supplierOffer.offer.leadTimeMin}-${supplierOffer.offer.leadTimeMax} ${supplierOffer.offer.timeUnit ? supplierOffer.offer.timeUnit.toLowerCase() : ''}`
                                  : supplierOffer.offer.leadTimeMin
                                    ? `${supplierOffer.offer.leadTimeMin} Min`
                                    : supplierOffer.offer.leadTimeMax
                                      ? `${supplierOffer.offer.leadTimeMax} Max`
                                      : ''}
                              </Text>
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Row gutter={8}>
                      <HasAccess role={PERMISSIONS.purchaseRequests.supplierOffers.read}>
                        <Col span={12}>
                          <Button
                            size={'large'}
                            block
                            onClick={() =>
                              navigate(
                                `${ADMIN_ROUTES.purchaseRequest.view}/${id}/${ADMIN_ROUTES.purchaseRequest.supplierOfferUpdate.replace(':source', SUPPLIER_OFFER_SOURCE.PR_DETAILS).replace(':supplierOfferId', supplierOffer.supplierOfferId)}`
                              )
                            }
                          >
                            View Offer
                          </Button>
                        </Col>
                      </HasAccess>
                      <HasAccess role={PERMISSIONS.purchaseRequests.supplierOffers.write}>
                        <Col span={12}>
                          <Button
                            disabled={supplierOffer.published || purchaseRequest?.isFinalStatus}
                            size={'large'}
                            block
                            type={'primary'}
                            onClick={() => onPublish(supplierOffer.supplierOfferId)}
                          >
                            {supplierOffer.published ? 'Published' : 'Publish'}
                          </Button>
                        </Col>
                      </HasAccess>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      <DeleteModal openModal={deleteModalState} onDeleteModalClose={onDelete} recordId={recordId} />
      <PublishSupplierOfferModal entityId={id} openModal={publishModalState} onClose={onPublish} recordId={recordId} />
    </>
  );
});
